import React, { useEffect, useState } from 'react'
import { Icon } from 'semantic-ui-react'

import { DeckUserEditorEntryType } from 'services/apiTypes/collaborators.types'
import { Owner } from 'services/apiTypes/comment.types'

import { useAppSelector } from 'redux/hooks'

import ToastService from 'services/toast.service'
import CollaboratorsService from 'services/collaborators.service'

import LineLoader from 'components/elements/LineLoader'
import UserAutocomplete from 'components/formElements/UserAutocomplete'
import PhatButton from 'components/formElements/PhatButton'

import { timeSince } from 'utils/time.utils'

import styles from './collaborators.module.scss'

const CollaboratorSettings = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [collaborators, setCollaborators] = useState(new Array<DeckUserEditorEntryType>())

  const [addingNewEditor, setAddingNewEditor] = useState(false)
  const [selectedEditor, setSelectedEditor] = useState<Owner | null>(null)

  const deckId = useAppSelector(state => state.deck.id)

  useEffect(() => {
    CollaboratorsService.list(deckId)
      .then(res => setCollaborators(res.results))
      .catch(setError)
      .finally(() => setLoading(false))
  }, [])

  const handleCreateNewEditor = (e?: React.FormEvent) => {
    e?.preventDefault()

    if (!selectedEditor) return ToastService.create('Missing selected user', 'Deck Settings', 'error')

    setLoading(true)

    return CollaboratorsService.post(deckId, selectedEditor.id)
      .then(newCollaborator => {
        setSelectedEditor(null)
        setAddingNewEditor(false)
        setCollaborators([...collaborators, newCollaborator])
      })
      .catch(setError)
      .finally(() => setLoading(false))
  }

  const handeRemoveCollaborator = (collaborationId: string | number) => {
    setLoading(true)

    return CollaboratorsService.delete(collaborationId)
      .then(() => setCollaborators(collaborators.filter(c => c.id !== collaborationId)))
      .catch(() => ToastService.create('Unable to remove collaborator', 'Deck Settings', 'error'))
      .finally(() => setLoading(false))
  }

  if (error) return <div>{error}</div>

  return (
    <div className={styles.container}>
      <p>
        This feature allows users to share their decks with others and update them in real time together. Setting
        someone as a collaborator grants them privileges to make any changes to the deck that an owner could outside of
        anything found in the Main Settings (above).
      </p>
      <p>
        Have any feedback?{' '}
        <a
          className={styles.link}
          href="https://archidekt.com/forum/thread/3552530"
          rel="noopener noreferrer"
          target="_blank">
          Leave those comments here
        </a>
        .
      </p>

      {!addingNewEditor && (
        <div className={styles.controls}>
          <PhatButton color="blue" onClick={() => setAddingNewEditor(true)}>
            Add new Collaborator
          </PhatButton>
        </div>
      )}

      {loading && <LineLoader />}

      {addingNewEditor && (
        <form onSubmit={handleCreateNewEditor}>
          <UserAutocomplete triggerClassName={styles.search} value={selectedEditor} onSelected={setSelectedEditor} />

          <div className={styles.formControls}>
            <PhatButton type="button" color="red" onClick={() => setAddingNewEditor(false)}>
              Cancel
            </PhatButton>
            <PhatButton type="button" color="green" onClick={() => handleCreateNewEditor()}>
              Add Collaborator
            </PhatButton>
          </div>
        </form>
      )}

      {collaborators.map((collaborator, index) => (
        <div key={index} className={styles.userRow}>
          <div>
            <img
              className={styles.avatar}
              src={collaborator.user.avatar}
              alt={`${collaborator.user.username} avatar`}
            />
            <b>{collaborator.user.username}</b>
          </div>
          <div>
            <div className={styles.metaInfo}>
              Added by {collaborator.createdBy.username} {timeSince(collaborator.createdAt)} ago
            </div>

            <PhatButton icon="close" onClick={() => handeRemoveCollaborator(collaborator.id)} color="red" />
          </div>
        </div>
      ))}
      {!collaborators.length && <></>}
    </div>
  )
}

export default CollaboratorSettings
